import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Columns2, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { API, errMsg } from "../lib/api";
import { VERDICTS } from "../lib/bopta";
import { deviceResults } from "../lib/device";

const fmtDate = (s) => new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" });
const LENS_LABEL = { staff: "Staff lens", manager: "Manager lens" };

const DeviceResults = () => {
  const ids = deviceResults();
  const lenses = ["staff", "manager"].filter((m) => ids[m]);
  if (lenses.length === 0) return null;
  return (
    <div className="mt-8 grid gap-3 sm:grid-cols-2">
      {lenses.map((m) => (
        <Link key={m} to={`/results/${ids[m]}`} className="panel flex items-center justify-between p-5 hover:border-[var(--teal)]" data-testid={`my-results-device-${m}`}>
          <span><span className="kicker">On this device · {LENS_LABEL[m]}</span><span className="mt-1 block font-semibold">Open your latest {m} result</span></span>
          <ArrowRight className="h-5 w-5 text-[var(--teal)]" />
        </Link>
      ))}
    </div>
  );
};

const CompareButton = ({ items }) => {
  const nav = useNavigate();
  const staff = items.find((r) => r.mode === "staff");
  const manager = items.find((r) => r.mode === "manager");
  if (!staff || !manager) return null;
  return (
    <button className="btn btn-main mt-4" onClick={() => nav(`/results/${staff.id}`, { state: { compareWith: manager.id } })} data-testid="my-results-compare">
      <Columns2 className="h-4 w-4" /> Compare my staff and manager views
    </button>
  );
};

const ResultItem = ({ r }) => (
  <Link to={`/results/${r.id}`} className="flex items-center justify-between border-t border-[var(--line)] py-4 hover:text-[var(--teal)]" data-testid={`my-result-item-${r.id}`}>
    <span>
      <span className="font-semibold">Level {r.level} · {r.level_name}</span>
      <span className="block text-xs muted">{fmtDate(r.created_at)} · {r.mode === "manager" ? "Manager lens" : "Staff lens"} · Score {Math.round(r.overall)}{r.verdict ? ` · ${VERDICTS[r.verdict][0]}` : ""}{r.team_name ? ` · ${r.team_name}${r.round ? ` round ${r.round}` : ""}` : ""}</span>
    </span>
    <ArrowRight className="h-4 w-4 shrink-0" />
  </Link>
);

const LookupForm = ({ onFound }) => {
  const [email, setEmail] = useState("");
  const [check, setCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      onFound((await API.post("/my-results", { email, check })).data);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="panel mt-6 space-y-4 p-6 sm:p-8" data-testid="my-results-form">
      <div><label className="text-sm font-medium">Email you used</label><Input type="email" required className="mt-1 h-11" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="my-results-email" /></div>
      <div>
        <label className="text-sm font-medium">Last 4 digits of your phone</label>
        <Input required className="mt-1 h-11" value={check} onChange={(e) => setCheck(e.target.value)} data-testid="my-results-check" />
        <p className="mt-1 text-xs muted">Skipped the phone number? Enter your full name exactly as you typed it.</p>
      </div>
      {err && <p className="text-sm text-[var(--orange)]" data-testid="my-results-error">{err}</p>}
      <button className="btn btn-main" disabled={busy} data-testid="my-results-submit"><Search className="h-4 w-4" /> {busy ? "Searching..." : "Find my results"}</button>
    </form>
  );
};

export default function MyResults() {
  const [items, setItems] = useState(null);
  return (
    <div className="wrap max-w-2xl py-16" data-testid="my-results-page">
      <p className="kicker">My results</p>
      <h1 className="mt-3 text-4xl font-extrabold sm:text-5xl">Find your past results.</h1>
      <p className="mt-3 muted">Enter the email you gave at the end of the assessment. We'll show every result saved under it.</p>
      <DeviceResults />
      {items ? (
        <div className="panel mt-6 p-6 sm:p-8" data-testid="my-results-list">
          <div className="flex items-center justify-between"><p className="kicker">{items.length} {items.length === 1 ? "result" : "results"} found</p><button className="text-sm text-[var(--teal)]" onClick={() => setItems(null)} data-testid="my-results-reset">Search again</button></div>
          <div className="mt-3">{items.map((r) => <ResultItem key={r.id} r={r} />)}</div>
          <CompareButton items={items} />
        </div>
      ) : <LookupForm onFound={setItems} />}
      <p className="mt-8 text-sm muted">Haven't taken it yet? <Link to="/assessment" className="text-[var(--teal)]" data-testid="my-results-cta">Take the assessment</Link></p>
    </div>
  );
}
